import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import InputField from "components/Input/InputField";
import companyData from "const/companyData.json";
import { currency } from "const/constants";
import useKothar from "context/useKothar";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { AiOutlineDelete } from "react-icons/ai";
import { toast } from "react-toastify";
import InvoiceTemplate from "./InvoiceTemplate";
import { CurrencyExchange } from "@mui/icons-material";
import { addDays } from "date-fns";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const defaultCompany = companyData?.[0] || null;

function moduleLabel(module) {
  if (module == null) return "Account";
  if (typeof module === "object") return module.label || module.value || "Account";
  return String(module);
}

function buildLineFromRow(rowData) {
  const amount = Number(
    rowData?.dueAmount ?? rowData?.totalDueAmount ?? rowData?.agreedAmount ?? 0
  );
  const desc = moduleLabel(rowData?.module);
  return {
    id: rowData?.uuid || rowData?.id || "line-0",
    description: desc,
    quantity: 1,
    price: amount,
    gst: "yes",
    total: amount,
  };
}

function normalizeInvoiceLines(lines) {
  if (!Array.isArray(lines)) return [];
  return lines.filter(Boolean).map((item, index) => {
    const quantity = Number(item?.quantity ?? 1);
    const price = Number(item?.price ?? item?.unitPrice ?? 0);
    const total = item?.total != null ? Number(item.total) : quantity * price;
    return {
      id: item?.id ?? item?.lineId ?? `line-${index}`,
      description: item?.description ?? item?.module ?? "Line item",
      quantity,
      price,
      gst: item?.gst === "no" || item?.gst === false || item?.gst === "false" ? "no" : "yes",
      total,
    };
  });
}

const GenerateInvoice = ({ open, onClose, rowData }) => {
  const dialogOpen =
    typeof open === "object" && open !== null ? Boolean(open.state) : Boolean(open);

  const [, { getIndividualAccountData, refetchInvoiceList }] = useKothar();
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: "Invoice",
  });

  const [data, setData] = useState({
    title: "",
    invoiceTo: "",
    paymentInfo: defaultCompany?.paymentInfo
      ? {
          accountNumber: defaultCompany.paymentInfo.accountNumber,
          accountName: defaultCompany.paymentInfo.accountName,
          bankDetails: defaultCompany.paymentInfo.bsb
            ? `BSB: ${defaultCompany.paymentInfo.bsb}`
            : defaultCompany.paymentInfo.bankName || "",
        }
      : {
          accountNumber: "",
          accountName: "",
          bankDetails: "",
        },
    invoices: [],
    contactNumber: "",
    abn: "",
    address: "",
    companyName: defaultCompany?.name || "",
    location: defaultCompany?.location || defaultCompany?.address || "",
    email: defaultCompany?.email || "",
    contact: defaultCompany?.contact || "",
    invoiceFromAbn: defaultCompany?.abn || "",
    status: false,
    dueDate: addDays(new Date(), 7).toISOString().split("T")[0],
    subTotal: 0,
    attachments: [],
    invoiceId: null,
    issueDate: new Date().toISOString(),
  });

  useEffect(() => {
    if (rowData?.hasAccountDetails != null && rowData?.clientAccountId != null) {
      getIndividualAccountData(rowData.hasAccountDetails, rowData.clientAccountId);
    }
  }, [rowData?.hasAccountDetails, rowData?.clientAccountId]);

  useEffect(() => {
    if (!rowData) return;

    const nextInvoices =
      Array.isArray(rowData?.invoices) && rowData.invoices.length > 0
        ? normalizeInvoiceLines(rowData.invoices)
        : [buildLineFromRow(rowData)];

    setData((prev) => ({
      ...prev,
      invoiceTo:
        rowData.invoiceTo ?? rowData.clientName ?? rowData?.clientData?.name ?? prev.invoiceTo,
      address: rowData.address ?? rowData?.clientData?.address ?? prev.address,
      contactNumber: rowData.contactNumber ?? rowData?.clientData?.number ?? prev.contactNumber,
      companyName: prev.companyName || defaultCompany?.name || "",
      location: prev.location || defaultCompany?.location || defaultCompany?.address || "",
      email: prev.email || defaultCompany?.email || "",
      contact: prev.contact || defaultCompany?.contact || "",
      invoiceFromAbn: prev.invoiceFromAbn || defaultCompany?.abn || "",
      paymentInfo: defaultCompany?.paymentInfo
        ? {
            accountNumber: defaultCompany.paymentInfo.accountNumber,
            accountName: defaultCompany.paymentInfo.accountName,
            bankDetails: defaultCompany.paymentInfo.bsb
              ? `BSB: ${defaultCompany.paymentInfo.bsb}`
              : "",
          }
        : prev.paymentInfo,
      invoices: nextInvoices,
      invoiceId: rowData?.clientAccountId ?? rowData?.id ?? prev.invoiceId,
    }));
  }, [rowData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleDelete = async (lineId) => {
    setData((prev) => ({
      ...prev,
      invoices: (prev.invoices || []).filter((inv) => inv.id !== lineId),
    }));
    toast.success("Line removed");
  };

  const handleInvoiceInput = (e, index) => {
    const { name, value } = e.target;
    setData((prevState) => {
      const list = [...(prevState.invoices || [])];
      const row = list[index];
      if (!row) return prevState;

      const next = { ...row, [name]: value };
      if (name === "quantity" || name === "price") {
        const qty = Number(next.quantity || 0);
        const price = Number(next.price || 0);
        next.total = qty * price;
      }
      list[index] = next;
      return { ...prevState, invoices: list };
    });
  };

  const printData = useMemo(() => {
    const invoices = (data.invoices || []).map((item) => {
      const qty = Number(item.quantity || 0);
      const price = Number(item.price || 0);
      const computedTotal = item.total != null ? Number(item.total) : qty * price;
      return {
        description: item.description,
        quantity: qty,
        price,
        gst: item.gst === "no" ? "no" : "yes",
        total: computedTotal,
      };
    });

    const subTotal = invoices.reduce(
      (sum, item) => sum + Number(item.quantity) * Number(item.price),
      0
    );

    return {
      ...data,
      invoices,
      subTotal,
      companyName: data.companyName || defaultCompany?.name,
      location: data.location || defaultCompany?.location,
      invoiceFromAbn: data.invoiceFromAbn || data.abn || defaultCompany?.abn,
      email: data.email || defaultCompany?.email,
      contact: data.contact || data.contactNumber || defaultCompany?.contact,
      paymentInfo:
        data.paymentInfo?.accountName != null
          ? data.paymentInfo
          : {
              accountName: defaultCompany?.paymentInfo?.accountName,
              accountNumber: defaultCompany?.paymentInfo?.accountNumber,
              bankDetails: defaultCompany?.paymentInfo?.bsb
                ? `BSB: ${defaultCompany.paymentInfo.bsb}`
                : "",
            },
      issueDate: data.issueDate || new Date().toISOString(),
      dueDate: data.dueDate || "",
      status: Boolean(data.status),
      invoiceId: data.invoiceId ?? rowData?.clientAccountId ?? "preview",
    };
  }, [data, rowData]);

  const { mutate } = useMutation(postData, {
    onSuccess() {
      refetchInvoiceList();
    },
    onError() {
      toast.error("Error Submitting Data");
    },
  });
  async function postData(payload) {
    await axios.post(`${API_URL}/invoice`, payload);
  }

  const handleGenerateInvoice = () => {
    if (!printData?.invoices?.length) {
      toast.error("Add at least one line item to print");
      return;
    }
    if (!data.invoiceTo?.trim()) {
      toast.error("Invoice to is required");
      return;
    }
    reactToPrintFn?.();
  };

  const handleGenerateAndSaveInvoice = () => {
    if (!printData?.invoices?.length) {
      toast.error("Add at least one line item to print");
      return;
    }
    if (!data.invoiceTo?.trim()) {
      toast.error("Invoice to is required");
      return;
    }
    reactToPrintFn?.();
    const payload = printData;
    delete payload.invoiceId;
    mutate({ ...payload });
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="font-bold uppercase">Generate Invoice</DialogTitle>
        <hr />
        <DialogContent>
          <div className="grid grid-cols-12 items-center gap-8">
            <div className="col-span-4 relative w-full mb-1">
              <InputField
                label="Invoice to"
                placeholder="Enter Invoice to"
                name="invoiceTo"
                required
                type="text"
                value={data?.invoiceTo}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-4 relative w-full mb-1">
              <InputField
                label="Address of the Receipt"
                placeholder="Address of the Receipt"
                name="address"
                required
                type="text"
                value={data?.address}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-4 relative w-full mb-1">
              <InputField
                label="Contact Number"
                placeholder="Contact Number"
                name="contactNumber"
                required
                type="text"
                value={data?.contactNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-4 relative w-full mb-1">
              <InputField
                label="Due Date"
                placeholder="Due Date"
                name="dueDate"
                required
                type="date"
                value={data?.dueDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-4 relative w-full mb-1">
              <InputField
                label="ABN of the Business (optional)"
                placeholder="ABN Number"
                name="abn"
                type="text"
                value={data?.abn ?? ""}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="mt-4">
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit price</TableCell>
                    <TableCell className="w-[220px]">GST</TableCell>
                    <TableCell className="w-[120px]">Total</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.invoices || []).map((invoice, index) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <InputField
                          label=""
                          name="description"
                          value={invoice.description ?? ""}
                          onChange={(e) => handleInvoiceInput(e, index)}
                          className="!mb-0"
                        />
                      </TableCell>
                      <TableCell>
                        <InputField
                          label=""
                          name="quantity"
                          type="number"
                          value={String(invoice.quantity ?? 1)}
                          onChange={(e) => handleInvoiceInput(e, index)}
                          className="!mb-0"
                        />
                      </TableCell>
                      <TableCell className="">
                        <InputField
                          label=""
                          name="price"
                          type="number"
                          startIcon={currency}
                          value={String(invoice.price ?? 0)}
                          onChange={(e) => handleInvoiceInput(e, index)}
                          className="!mb-0"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            flexWrap: "nowrap",
                          }}
                          size="small"
                        >
                          <RadioGroup
                            row
                            name="gst"
                            onChange={(e) => handleInvoiceInput(e, index)}
                            value={invoice.gst === "no" ? "no" : "yes"}
                            sx={{
                              "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                              gap: 1,
                            }}
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio size="small" />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio size="small" />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-semibold"> {currency} </span>
                        {Number(
                          invoice.total ?? Number(invoice.price) * Number(invoice.quantity)
                        ).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Tooltip title="Remove line">
                            <IconButton color="error" onClick={() => handleDelete(invoice.id)}>
                              <AiOutlineDelete />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </DialogContent>
        <DialogActions className="py-2">
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerateAndSaveInvoice}>
            Print & Save Invoice
          </Button>
          <Button variant="contained" onClick={handleGenerateInvoice}>
            Print / Preview Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Off-screen: same pattern as Invoice Add – ref used by react-to-print */}
      <div
        className="print:hidden"
        style={{ position: "absolute", left: "-9999px", top: 0, width: "210mm" }}
      >
        <InvoiceTemplate printData={printData} contentRef={contentRef} />
      </div>
    </>
  );
};

export default GenerateInvoice;
